import { callBankNameEndpoint } from 'registration/BankInfo/BankInfoApiCalls';
import { debounce } from 'lodash';
import { emailBriteVerifyValidation } from 'validations/AccountInfoValidations';
import { jukeboxRequest } from 'helpers/JukeboxRequest';
import { REQUIRED_ERROR, SET_DATA, SET_ERROR } from 'helpers/Constants';
import moment from 'moment';
import validations from 'validations/validations';

const baseValidate = (dispatch, getState, id) => {
  const state = getState();

  if (validations[id]) {
    const result = validations[id](state);

    dispatch({ type: SET_ERROR, payload: result });

    if (
      id === 'paymentFrequency' &&
      state.paymentFrequency !== 'twice_per_month'
    ) {
      dispatch({ type: SET_ERROR, payload: { nextPayrollDate: '' } });
    }
    return result;
  }
  return {};
};

const debouncedValidate = debounce(baseValidate, 600, { trailing: true });

export const validateAction = (id) => {
  return (dispatch, getState) => baseValidate(dispatch, getState, id);
};

export const debouncedValidateAction = (id) => {
  return (dispatch, getState) => debouncedValidate(dispatch, getState, id);
};

export const setSpouseSameAddress = () => {
  return (dispatch, getState) => {
    const state = getState();
    dispatch({
      type: SET_DATA,
      payload: {
        spouseSameAddress:
          state.city === state.spouseCity &&
          state.zip === state.spouseZip &&
          state.state === state.spouseState &&
          state.homeStreetAddress === state.spouseAddress
      }
    });
  };
};

export const validateDate = (id, minDate, maxDate) => {
  return (dispatch, getState) => {
    const state = getState();
    if (validations.date) {
      const result = validations.date(id, state, minDate, maxDate);
      dispatch({ type: SET_ERROR, payload: result });
      return result;
    }
    return {};
  };
};

export const checkNextPayrollDate = () => {
  return (dispatch, getState) => {
    const state = getState();
    const lastPayrollDate = moment(state.lastPayrollDate, 'MM/DD/YYYY');

    if (state.paymentFrequency === 'twice_per_month') {
      dispatch(
        validateDate(
          'nextPayrollDate',
          lastPayrollDate.clone().add(10, 'days'),
          lastPayrollDate.clone().add(18, 'days')
        )
      );
    }
  };
};

export const validateState = (id) => {
  return (dispatch, getState) => {
    if (validations[id]) {
      const state = getState();
      if (id === 'state' && state[id] !== 'WI') {
        dispatch({
          type: SET_ERROR,
          payload: {
            maritalStatus: '',
            spouseFirstName: '',
            spouseLastName: '',
            spouseAddress: '',
            spouseCity: '',
            spouseState: '',
            spouseZip: ''
          }
        });
      }

      const result = validations[id](state);
      dispatch({ type: SET_ERROR, payload: result });
      return result;
    }
    return {};
  };
};

export const handleBlurRoutingNumber = (newRoutingNumber = false) => {
  return async (dispatch, getState) => {
    const state = getState();
    const bankNameId = newRoutingNumber ? 'newBankName' : 'bankName';
    const routingNumberId = newRoutingNumber
      ? 'newRoutingNumber'
      : 'routingNumber';

    if (!state.errors[routingNumberId] && state[routingNumberId]) {
      dispatch({
        type: SET_DATA,
        payload: { [`${bankNameId}Visible`]: true }
      });
      try {
        const bankName = await callBankNameEndpoint(state[routingNumberId]);
        dispatch({
          type: SET_DATA,
          payload: { [bankNameId]: bankName || '' }
        });
        dispatch({
          type: SET_DATA,
          payload: { [`${bankNameId}Disabled`]: Boolean(bankName) }
        });
        if (bankName) {
          dispatch({
            type: SET_ERROR,
            payload: { [bankNameId]: '', [routingNumberId]: '' }
          });
        }
      } catch (e) {
        window.Raven.captureException(e);
        dispatch({
          type: SET_DATA,
          payload: { [bankNameId]: '' }
        });
      }
    } else {
      dispatch({
        type: SET_DATA,
        payload: { [`${bankNameId}Visible`]: false }
      });
      dispatch({
        type: SET_DATA,
        payload: { [bankNameId]: '' }
      });
      dispatch({
        type: SET_ERROR,
        payload: { [bankNameId]: '' }
      });
    }
  };
};

export const validateIncomeSource = () => {
  return (dispatch, getState) => {
    if (validations.primaryIncomeSource) {
      const state = getState();
      if (
        state.primaryIncomeSource !== 'employment' &&
        state.primaryIncomeSource !== 'self_employed'
      ) {
        dispatch({
          type: SET_ERROR,
          payload: {
            employmentStatus: ''
          }
        });
      }

      const result = validations.primaryIncomeSource(state);
      dispatch({ type: SET_ERROR, payload: result });
      return result;
    }
    return {};
  };
};

export const emailBriteVerifyAction = () => {
  return async (dispatch, getState) => {
    dispatch(validateAction('email'));
    const state = getState();
    if (!state.errors.email) {
      const result = await emailBriteVerifyValidation(state);
      dispatch({ type: SET_ERROR, payload: result });
    }
  };
};

export const emailValidations = async (
  dispatch,
  getState,
  validate,
  emailBriteVerifyValidate
) => {
  dispatch(validate('email'));
  const state = getState();
  if (!state.errors.email) {
    const result = await emailBriteVerifyValidate(state);
    dispatch({ type: SET_ERROR, payload: result });
  }
};

const debounceEmailValidations = debounce(emailValidations, 500, {
  trailing: true
});

export const debounceEmailBriteVerifyAction = (value, id, isOnMount) => {
  return (dispatch, getState) => {
    const email = getState().email;
    if (email !== '') {
      dispatch({
        type: SET_ERROR,
        payload: { email: 'Please wait for email validation' }
      });
      debounceEmailValidations(
        dispatch,
        getState,
        validateAction,
        emailBriteVerifyValidation
      );
    } else if (email === '' && !isOnMount) {
      dispatch({
        type: SET_ERROR,
        payload: { email: REQUIRED_ERROR }
      });
    }
  };
};

const debounceJukeboxRequest = debounce(
  async (
    dispatch,
    email,
    state,
    ssn,
    hideSsn,
    type,
    firstName,
    lastName,
    zip
  ) => {
    const res = await jukeboxRequest(
      email,
      state,
      ssn,
      hideSsn,
      type,
      firstName,
      lastName,
      zip
    );

    dispatch({
      type: SET_DATA,
      payload: {
        productOwner: res.owner,
        productCode: res.code,
        productTurnupVendor: res.turnupVendor,
        waitingForJukebox: false,
        minLoanAmount: res.minLoanAmount,
        maxLoanAmount: res.maxLoanAmount
      }
    });
  },
  500,
  {
    trailing: true
  }
);

export const debounceJukeboxRequestAction = () => {
  return (dispatch, getState) => {
    const store = getState();
    const email = store.email;
    const state = store.state;
    const ssn = store.socialSecurityNumber;
    const firstName = store.firstName;
    const lastName = store.lastName;
    const zip = store.zip;

    if (
      state &&
      ssn &&
      firstName &&
      lastName &&
      zip &&
      !validations.state(store).state &&
      !validations.socialSecurityNumber(store).socialSecurityNumber &&
      !validations.firstName(store).firstName &&
      !validations.lastName(store).lastName &&
      !validations.zip(store).zip
    ) {
      dispatch({
        type: SET_DATA,
        payload: {
          waitingForJukebox: true
        }
      });

      debounceJukeboxRequest(
        dispatch,
        email,
        state,
        ssn,
        store.hideSsn,
        store.type,
        firstName,
        lastName,
        zip
      );
    }
  };
};

export const debounceJukeboxRequestActionWithoutCheck = () => {
  return (dispatch, getState) => {
    const store = getState();

    dispatch({
      type: SET_DATA,
      payload: {
        waitingForJukebox: true
      }
    });

    debounceJukeboxRequest(
      dispatch,
      store.email,
      store.state,
      store.ssn,
      store.hideSsn,
      store.type,
      store.firstName,
      store.lastName,
      store.zip
    );
  };
};

export const makeAnimationStartHandler = (stateSetter) => (e) => {
  const autofilled = !!e.target?.matches('*:-webkit-autofill');
  if (e.animationName === 'mui-auto-fill') {
    stateSetter(autofilled);
  }

  if (e.animationName === 'mui-auto-fill-cancel') {
    stateSetter(autofilled);
  }
};
